// ============================================================================
// Who owns a record — the POSITION, and optionally a named assignee.
//
// ⛔ OWNERSHIP IS A POSITION, NEVER A PERSON. TCSD-0074 / PR #362 removed
// `bryan_approved` for this reason; `hr_owner` was the same defect surviving in
// a column, and TCSD-0162 replaced it with the pair below, copied from
// `separation_checklist_items` rather than invented:
//
//     owner_position_key  the desk that owns it  — always set
//     assigned_to_eid     a named assignee       — optional
//
// The position is the owner. The person is an assignment. Change who holds the
// desk and every record follows; name somebody on one record and only that one
// moves.
//
// ⛔ THE LABEL ALWAYS LEADS WITH THE POSITION, even when somebody is assigned.
// "HR · Antonela Chesini" — never "Antonela Chesini" alone. If the name were
// allowed to stand on its own the screen would read exactly like the column we
// just retired, and the next person would reasonably assume ownership is
// personal.
// ============================================================================

/**
 * The position vocabulary, shared verbatim with
 * `separation_checklist_items.owner_position_key`'s CHECK constraint. ⛔ Adding a
 * key here without the matching migration produces a label for a value the
 * database will refuse.
 */
export const POSITION_LABELS: Record<string, string> = {
  coo: 'COO',
  hr: 'HR',
  finance: 'Finance',
  national_am: 'National AM',
  supervisor: 'Supervisor',
};

/** Human label for a position key. An unknown key renders as itself, never as blank. */
export function positionLabel(key: string | null | undefined): string {
  if (!key) return '—';
  return POSITION_LABELS[key] ?? key;
}

export interface PositionOwned {
  owner_position_key: string | null;
  assigned_to_eid: string | null;
}

/**
 * How a record's ownership reads on screen.
 *
 *   unassigned          → "HR"
 *   assigned, name known → "HR · Antonela Chesini"
 *   assigned, name not resolved → "HR · EID-0005"
 *
 * `names` maps employee_id → display name; pass whatever the caller already has
 * loaded. A missing name falls back to the id rather than hiding the assignment.
 */
export function ownerLabel(
  row: PositionOwned,
  names?: ReadonlyMap<string, string> | Record<string, string>,
): string {
  const position = positionLabel(row.owner_position_key);
  const eid = row.assigned_to_eid;
  if (!eid) return position;
  let resolved: string | undefined;
  if (names instanceof Map) resolved = names.get(eid);
  else if (names) resolved = (names as Record<string, string>)[eid];
  return `${position} · ${resolved || eid}`;
}
